import { type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  buildFounderBreadcrumbLabels,
  buildFounderSectionHubPath,
  resolveFounderSection,
} from "../../founder/founderNavigation";

type FounderWorkspaceChromeProps = {
  pathname: string;
  title: string;
  lead?: string;
  backTo?: string;
  backLabel?: string;
  children: ReactNode;
};

export function FounderWorkspaceChrome({
  pathname,
  title,
  lead,
  backTo,
  backLabel = "Back",
  children,
}: FounderWorkspaceChromeProps) {
  const crumbs = buildFounderBreadcrumbLabels(pathname);
  const section = resolveFounderSection(pathname);
  const sectionBackPath = section ? buildFounderSectionHubPath(section.id) : undefined;
  const resolvedBackTo = backTo ?? sectionBackPath;
  const resolvedBackLabel = backLabel ?? (backTo ? "Back" : "Back to section");
  return (
    <section className="oac-founder-workspace">
      <nav aria-label="Location" className="oac-founder-workspace__crumbs">
        {crumbs.map((label, index) => (
          <span key={`${label}-${index}`}>
            {index > 0 ? <span className="oac-founder-workspace__crumb-sep">→</span> : null}
            <span
              className={
                index === crumbs.length - 1
                  ? "oac-founder-workspace__crumb oac-founder-workspace__crumb--current"
                  : "oac-founder-workspace__crumb"
              }
            >
              {label}
            </span>
          </span>
        ))}
      </nav>

      <header className="oac-founder-workspace__header">
        <div className="oac-founder-workspace__heading-row">
          {resolvedBackTo ? (
            <Link className="oac-founder-workspace__back" to={resolvedBackTo}>
              ← {resolvedBackLabel}
            </Link>
          ) : null}
          <div>
            <h1 className="oac-founder-workspace__title">{title}</h1>
            {lead ? <p className="oac-founder-workspace__lead">{lead}</p> : null}
          </div>
        </div>
      </header>

      <div className="oac-founder-workspace__body">{children}</div>
    </section>
  );
}

export function FounderGapNotice({
  title,
  detail,
}: {
  title: string;
  detail: string;
}) {
  return (
    <div className="oac-founder-gap">
      <p className="oac-founder-gap__title">{title}</p>
      <p className="oac-founder-gap__detail">{detail}</p>
    </div>
  );
}

/** Product-facing notice for capabilities not yet available in the Control Centre. */
export function FounderCapabilityPending({
  title,
  summary,
}: {
  title: string;
  summary: string;
}) {
  return (
    <div className="oac-founder-capability-pending">
      <p className="oac-founder-capability-pending__title">{title}</p>
      <p className="oac-founder-capability-pending__summary">{summary}</p>
    </div>
  );
}

export function FounderLoadingState({ label = "Loading..." }: { label?: string }) {
  return <p className="oac-founder-state">{label}</p>;
}

export function FounderErrorState({ message }: { message: string }) {
  return <p className="oac-founder-state oac-founder-state--error">{message}</p>;
}

export function FounderEmptyState({ message }: { message: string }) {
  return <p className="oac-founder-state oac-founder-state--empty">{message}</p>;
}

export function useFounderBackNavigation() {
  const navigate = useNavigate();
  return (path: string) => navigate(path);
}